import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Star, 
  Award, 
  Shield, 
  CheckCircle, 
  Clock, 
  Users, 
  Menu, 
  X,
  Instagram,
  Facebook,
  Twitter,
  Play
} from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";

const showcaseImages = [
  '/business-floor.jpg',
  '/residential.jpg',
  '/carpet-red.jpeg',
  '/office-wide.jpg',
  '/residential.jpg',
];

const navDots = [
  { id: 'home', label: 'Home' },
  { id: 'services', label: 'Services' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'about', label: 'About' },
  { id: 'contact', label: 'Contact' },
];

const GalleryShowcase = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % showcaseImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full h-96 rounded-3xl overflow-hidden shadow-2xl border-4 border-white/20">
      <AnimatePresence mode="wait">
        <motion.img
          key={index}
          src={showcaseImages[index]}
          alt="Premium Flooring Showcase"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 1, ease: 'easeInOut' }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      
      {/* Navigation Dots */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {showcaseImages.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              i === index ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });


  const { scrollY } = useScroll();
  const headerOpacity = useTransform(scrollY, [0, 100], [1, 0.95]);
  const headerY = useTransform(scrollY, [0, 100], [0, -10]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSuccess(true);
      setIsSubmitting(false);
      toast({
        title: "✅ Quote Request Submitted",
        description: "Thank you for your enquiry. We'll get back to you within 24 hours.",
      });
    }, 2000);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Load Elfsight script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://static.elfsight.com/platform/platform.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <motion.header
        style={{ opacity: headerOpacity, y: headerY, zIndex: 1000001 }}
        className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-lg border-b border-white/10 shadow-sm h-20 xl:h-20"
      >
        <div className="container mx-auto px-6 py-4 h-full flex items-center">
          <div className="flex items-center justify-between w-full h-full">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center flex-shrink-0"
            >
              <h1 className="text-2xl md:text-3xl font-bold text-white">Prima Floors</h1>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center space-x-6">
              {['Home', 'Services', 'Gallery', 'About', 'Contact'].map((item) => (
                <motion.button
                  key={item}
                  whileHover={{ y: -1, color: '#87CEEB' }}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-white hover:text-[#87CEEB] font-medium transition-all duration-300 relative group text-sm uppercase tracking-wide px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
                >
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#87CEEB] transition-all duration-300 group-hover:w-full"></span>
                </motion.button>
              ))}
              {/*<a
                href="https://blog.primafloors.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-[#87CEEB] font-medium transition-all duration-300 relative group text-sm uppercase tracking-wide px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
              >
                Blog
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#87CEEB] transition-all duration-300 group-hover:w-full"></span>
              </a>*/}
            </nav>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="hidden xl:flex items-center space-x-4"
            >
              <a
                href="tel:07938560536"
                className="inline-flex items-center border border-white/80 text-white bg-white/10 hover:border-[#87CEEB] hover:text-[#87CEEB] hover:bg-white/20 transition-all duration-300 font-medium rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
                style={{ textDecoration: 'none' }}
              >
                <Phone className="w-4 h-4 mr-2" />
                07938 560536
              </a>
              <Button
                className="bg-[#87CEEB] hover:bg-[#75B8D9] text-black shadow-lg hover:shadow-xl transition-all duration-300 font-semibold"
                onClick={() => scrollToSection('contact')}
              >
                Get Quote
              </Button>
            </motion.div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden p-2 rounded-lg hover:bg-[#87CEEB]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
            >
              {isMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="xl:hidden bg-black/90 border-t border-white/10 shadow-lg"
            >
              <div className="px-6 py-6 space-y-4">
                {['Home', 'Services', 'Gallery', 'About', 'Contact'].map((item) => (
                  <button
                    key={item}
                    onClick={() => {
                      setIsMenuOpen(false);
                      setTimeout(() => scrollToSection(item.toLowerCase()), 250);
                    }}
                    className="block w-full text-left text-white hover:text-[#87CEEB] font-medium py-3 transition-colors text-sm uppercase tracking-wide rounded focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
                  >
                    {item}
                  </button>
                ))}
                {/*<a
                  href="https://blog.primafloors.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-left text-white hover:text-[#87CEEB] font-medium py-3 transition-colors text-sm uppercase tracking-wide rounded focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
                >
                  Blog
                </a>*/}
                <div className="pt-4 border-t border-white/10">
                  <a
                    href="tel:07938560536"
                    className="w-full inline-flex items-center justify-center border-2 border-white/80 text-white bg-white/10 hover:border-[#87CEEB] hover:text-[#87CEEB] hover:bg-white/20 font-semibold transition-all duration-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
                    style={{ textDecoration: 'none' }}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    07938 560536
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src="/business-floor.jpg"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/40"></div>

        {/* Geometric Pattern Overlay */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-32 h-32 border-2 border-white/20 rounded-full"></div>
          <div className="absolute top-40 right-32 w-24 h-24 border-2 border-white/20 transform rotate-45"></div>
          <div className="absolute bottom-32 left-32 w-20 h-20 border-2 border-white/20 rounded-full"></div>
        </div>

        {/* Floating Elements */}
        <motion.div
          animate={{ y: [-20, 20, -20] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-32 left-16 w-16 h-16 bg-[#87CEEB]/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-[#87CEEB]/30"
        >
          <Star className="w-8 h-8 text-[#87CEEB]" />
        </motion.div>

        <motion.div
          animate={{ y: [20, -20, 20] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-32 right-20 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30"
        >
          <Award className="w-6 h-6 text-white" />
        </motion.div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid xl:grid-cols-2 gap-8 xl:gap-12 items-center">
            {/* Left Content - Headline, Subheadline, CTA */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center xl:text-left"
            >
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-2xl sm:text-3xl md:text-5xl xl:text-7xl font-bold text-white mb-3 md:mb-4 xl:mb-6 leading-tight mt-6"
              >
                Transform Your Space with{' '}
                <span className="text-[#87CEEB] drop-shadow-[0_2px_12px_rgba(135,206,235,0.5)]">Prima Floors</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="text-base sm:text-lg md:text-2xl xl:text-3xl text-white/90 mb-6 md:mb-8 xl:mb-12 max-w-lg leading-relaxed mx-auto xl:mx-0 text-center xl:text-left"
              >
                Expert flooring solutions that elevate your home and business with unmatched quality and craftsmanship.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center xl:justify-start mb-8 md:mb-12"
              >
                <Button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-[#87CEEB] hover:bg-[#75B8D9] text-black px-6 sm:px-10 py-4 sm:py-6 text-base sm:text-lg font-semibold rounded-xl shadow-2xl hover:shadow-[#87CEEB]/25 transition-all duration-300 border-0 focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
                >
                  Get Free Quote
                </Button>
                <Button
                  variant="outline"
                  className="border-white/80 text-white bg-white/10 hover:border-[#87CEEB] hover:text-[#87CEEB] hover:bg-white/20 px-6 sm:px-10 py-4 sm:py-6 text-base sm:text-lg font-semibold rounded-xl shadow-2xl transition-all duration-300"
                >
                  <Play className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  View Gallery
                </Button>
              </motion.div>

              {/* Trust Indicators as badges */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="flex flex-wrap gap-3 sm:gap-4 justify-center xl:justify-start"
              >
                <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-xs sm:text-sm font-medium gap-2">
                  <span className="w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full"></span>
                  20+ Years Experience
                </span>
                <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-xs sm:text-sm font-medium gap-2">
                  <span className="w-2 h-2 sm:w-3 sm:h-3" style={{ background: '#3B82F6', borderRadius: '9999px' }}></span>
                  5.0 on Google
                </span>
              </motion.div>
            </motion.div>

            {/* Right Content - Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative mt-8 xl:mt-0"
            >
              <GalleryShowcase />
              {/* Floating Elements */}
              <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-4 -right-4 w-20 h-20 bg-[#87CEEB]/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-[#87CEEB]/30"
              >
                <Star className="w-8 h-8 text-[#87CEEB]" />
              </motion.div>
              <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30"
              >
                <Award className="w-6 h-6 text-white" />
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center text-white"
          >
            <span className="text-sm mb-2">Scroll to explore</span>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
              className="w-1 h-3 bg-white/60 rounded-full mt-2"
            ></motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Premium Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From luxury vinyl to hardwood, we offer comprehensive flooring solutions tailored to your needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { name: 'Luxury Vinyl Tile Flooring', image: "/luxury-vinyl-3.jpg" },
              { name: 'Moisture Barrier Installation', image: "/moisture-barrier.jpg" },
              { name: 'Rubber Flooring', image: "/rubber.jpg" },
              { name: 'Vinyl Flooring', image: "/vinyl-3.jpg" },
              { name: 'Cap and Cove Safety Flooring', image: "/cap-and-cove.jpg" },
              { name: 'Stair Nosing & Staircase Flooring', image: "/stair-nosing.jpg" },
              { name: 'Flooring Maintenance & Repairs', image: "/office-room.jpg" },
              { name: 'Floor Sanding & Refinishing', image: "/sanding.jpg" },
              { name: 'Carpet Tiles', image: "/carpet-tiles-2.jpg" },
              { name: 'Carpet Installation and Replacement', image: "/carpet-office.jpg" },
              { name: 'Barrier Matting', image: "/barrier-matt.jpg" },
              { name: 'Hardwood Flooring Installation', image: "/residential.jpg" },
              { name: 'Residential Flooring', image: "/residential-2.jpg" },
              { name: 'Commercial Flooring', image: "/office-wide.jpg" },
              { name: 'Hospitality Repairs', image: "/hospitality.jpg" },

            ].map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="group"
              >
                <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-white">
                  <CardContent className="p-0">
                    <div className="w-full h-56 rounded-t-2xl overflow-hidden">
                      <img src={service.image} alt={service.name} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 text-center">{service.name}</h3>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="pt-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Recent Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover the quality and craftsmanship that sets Prima Floors apart from the competition.
            </p>
          </motion.div>
          {/* Instagram Feed Embed */}
          <div className="flex justify-center">
            <div className="relative w-full max-w-6xl">
              <div className="elfsight-app-b2ffccd9-0316-43fc-9876-2b2817e2e887" data-elfsight-app-lazy></div>
              {/* White overlay to mask badge */}
              <div className="absolute left-0 right-0 bottom-0 h-12 bg-gray-50 z-10 pointer-events-none" style={{ zIndex: 100000, background: '#F9FAFB'}} />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-br from-[#F7F8FA] via-white to-[#F7F8FA] relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left: Headline, Subheadline, Features */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                <span className="text-gray-900">Why Choose </span>
                <span className="text-[#87CEEB] drop-shadow-[0_2px_12px_rgba(135,206,235,0.2)]">Prima Floors?</span>
              </h2>
              <p className="text-xl text-gray-600 mb-10 max-w-xl">
                With over two decades of experience, Prima Floors delivers top class flooring solutions with unmatched quality, reliability, and customer satisfaction.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Feature 1 */}
                <motion.div
                  whileHover={{ scale: 1.04 }}
                  className="flex items-center gap-4 bg-white/70 backdrop-blur-md border border-[#87CEEB]/10 rounded-2xl px-6 py-5 shadow-md hover:shadow-lg transition-all"
                >
                  <div className="p-3 bg-[#87CEEB]/10 rounded-xl">
                    <Users className="w-8 h-8 text-[#87CEEB]" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">Tailored Solutions</div>
                    <div className="text-sm text-gray-500">Free consultations and tailored advice</div>
                  </div>
                </motion.div>
                {/* Feature 2 */}
                <motion.div
                  whileHover={{ scale: 1.04 }}
                  className="flex items-center gap-4 bg-white/70 backdrop-blur-md border border-[#87CEEB]/10 rounded-2xl px-6 py-5 shadow-md hover:shadow-lg transition-all"
                >
                  <div className="p-3 bg-[#87CEEB]/10 rounded-xl">
                    <Shield className="w-8 h-8 text-[#87CEEB]" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">Fully Insured & Licensed</div>
                    <div className="text-sm text-gray-500">Complete peace of mind</div>
                  </div>
                </motion.div>
                {/* Feature 3 */}
                <motion.div
                  whileHover={{ scale: 1.04 }}
                  className="flex items-center gap-4 bg-white/70 backdrop-blur-md border border-[#87CEEB]/10 rounded-2xl px-6 py-5 shadow-md hover:shadow-lg transition-all"
                >
                  <div className="p-3 bg-[#87CEEB]/10 rounded-xl">
                    <Clock className="w-8 h-8 text-[#87CEEB]" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">On-Time Completion</div>
                    <div className="text-sm text-gray-500">Guaranteed project timelines</div>
                  </div>
                </motion.div>
                {/* Feature 4 */}
                <motion.div
                  whileHover={{ scale: 1.04 }}
                  className="flex items-center gap-4 bg-white/70 backdrop-blur-md border border-[#87CEEB]/10 rounded-2xl px-6 py-5 shadow-md hover:shadow-lg transition-all"
                >
                  <div className="p-3 bg-[#87CEEB]/10 rounded-xl">
                    <Star className="w-8 h-8 text-[#87CEEB]" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">5.0 on Google</div>
                    <div className="text-sm text-gray-500">Hundreds of 5-star reviews</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Right: Image + Glassy Stats Overlay */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative flex justify-center hidden md:block"
            >
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white/80">
                  <img src="/luxury-vinyl.webp" alt="Prima Floors Team or Project" className="object-cover w-full h-96" />
                </div>
                {/* Glassy Stats Overlay */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-11/12 bg-white/80 backdrop-blur-md border border-[#87CEEB]/10 rounded-2xl shadow-lg flex flex-col md:flex-row items-center justify-center gap-8 px-8 py-6">
                  <div className="flex flex-col items-center">
                    <div className="text-3xl font-bold text-[#87CEEB] mb-1">20+</div>
                    <div className="text-sm text-center text-gray-700">Years Experience</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="text-3xl font-bold text-[#87CEEB] mb-1">5.0</div>
                    <div className="text-sm text-center text-gray-700">on Google</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="text-3xl font-bold text-[#87CEEB] mb-1">1000+</div>
                    <div className="text-sm text-center text-gray-700">Projects Completed</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-[#F7F8FA] via-white to-[#F7F8FA] relative overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get Your Free Quote
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ready to transform your space? Contact us today for a free consultation from a member of our team.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <Card className="shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
              <CardContent className="p-8">
                <form action="https://formsubmit.co/hello@primafloorslondon.co.uk" method="post">
                  {/* Name - full width */}
                  <div>
                    <Label htmlFor="name" className="text-lg font-medium text-gray-800">Full Name*</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-2 py-4 px-4 text-lg rounded-xl border-gray-300 focus:border-[#87CEEB] focus:ring-2 focus:ring-[#87CEEB]/20 transition-all"
                      placeholder="Your full name"
                    />
                  </div>
                  {/* Email & Phone - side by side on md+ */}
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <Label htmlFor="email" className="text-lg font-medium text-gray-800">Email*</Label>
                      <Input
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="mt-2 py-4 px-4 text-lg rounded-xl border-gray-300 focus:border-[#87CEEB] focus:ring-2 focus:ring-[#87CEEB]/20 transition-all"
                        placeholder="you@email.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-lg font-medium text-gray-800">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-2 py-4 px-4 text-lg rounded-xl border-gray-300 focus:border-[#87CEEB] focus:ring-2 focus:ring-[#87CEEB]/20 transition-all"
                        placeholder="07000 000000"
                      />
                    </div>
                  </div>
                  {/* Message - full width */}
                  <div>
                    <Label htmlFor="message" className="text-lg font-medium text-gray-800">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      name="message"
                      onChange={e => setFormData({ ...formData, message: e.target.value })}
                      className="mt-2 min-h-[120px] py-4 px-4 text-lg rounded-xl border-gray-300 focus:border-[#87CEEB] focus:ring-2 focus:ring-[#87CEEB]/20 transition-all"
                      placeholder="Tell us about your project..."
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#87CEEB] hover:bg-[#75B8D9] text-black py-5 text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 mt-2"
                  >
                    Get Free Quote
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-2xl font-bold text-white">Prima Floors</h3>
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                Premium flooring solutions in London. Transform your space with our expert 
                craftsmanship and innovative flooring solutions.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-[#87CEEB] transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-[#87CEEB] transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-[#87CEEB] transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Residential Flooring</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Commercial Flooring</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  07938 560536
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  hello@primafloorslondon.co.uk
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  86-90 Paul St, London EC2A 4NE
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Kensington, Chelsea, Islington, Camden, Clapham, Battersea, Hampstead, Notting Hill, Mayfair, Westminster, Southwark, Richmond, and Wandsworth, and more.
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Prima Floors. All rights reserved.</p>
            <p className="mt-4">
              <a href="https://flooristiq.co.uk" className="text-gray-400 hover:text-white transition-colors">
                flooristiq.co.uk
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home; 